import React from 'react';
import { useCms, useCmsLoaded } from '../context/CmsContext';

const defaultPlans = [
  {
    name: 'SCALE',
    tagline: 'Para começar',
    price: 'R$ 5.990',
    period: '/mês*',
    features: [
      'Controle de processos',
      'Definição de fluxos operacionais',
      'Setores e responsáveis',
      'Suporte online',
    ],
    cta: 'Solicitar Proposta',
    style: 'border-gray-200 bg-white',
    ctaStyle: 'bg-blue-900 hover:bg-blue-800 text-white',
    isPopular: false,
  },
  {
    name: 'PERFORM',
    tagline: 'Mais escolhido',
    price: 'R$ 12.670',
    period: '/mês*',
    features: [
      'Todas as funcionalidades do SCALE',
      'Requerimento com geolocalização',
      'Painel de requisições',
      'Demandas de comunidades',
      'Suporte prioritário',
    ],
    cta: 'Solicitar Proposta',
    style: 'shadow-2xl',
    styleObj: { borderColor: '#F47920', background: '#F47920', boxShadow: '0 25px 50px rgba(244,121,32,0.3)' },
    ctaStyle: 'bg-white text-orange-600',
    ctaHover: '#fff7ed',
    isPopular: true,
  },
  {
    name: 'MASTER',
    tagline: 'Completo',
    price: 'R$ 17.000',
    period: '/mês*',
    features: [
      'Todas as funcionalidades do PERFORM',
      'Planejamento Estratégico (KPI/OKR)',
      'Projetos com Plano de ação',
      'Controle de produtividade',
      'Painel de indicadores',
      'Suporte 24/7',
    ],
    cta: 'Solicitar Proposta',
    style: 'border-gray-200 bg-white',
    ctaStyle: 'bg-blue-900 hover:bg-blue-800 text-white',
    isPopular: false,
  },
];

const CheckIcon = ({ inverted }) => (
  <svg className={`w-4 h-4 flex-shrink-0 ${inverted ? 'text-blue-200' : 'text-blue-600'}`} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
);

const PlanosSection = () => {
  const cms = useCms('planos', {});
  const cmsLoaded = useCmsLoaded();
  const hidePrice = !cmsLoaded || cms.hidePrice;
  const heading = cms.heading || 'Escolha o plano ideal para sua gestão';
  const subheading = cms.subheading || 'Soluções escaláveis a partir de 100 usuários.';
  const footnote = cms.footnote || '* Valores para 100 usuários. Também disponíveis planos para 200 e 300 usuários.';

  const plans = defaultPlans.map((plan, i) => {
    const c = (cms.plans || [])[i] || {};
    return {
      ...plan,
      name: c.name || plan.name,
      price: c.price || plan.price,
      period: c.period || plan.period,
      features: c.features && c.features.length > 0 ? c.features : plan.features,
      cta: c.buttonText || plan.cta,
      isPopular: c.isPopular !== undefined ? c.isPopular : plan.isPopular,
    };
  });

  return (
    <section id="planos" className="py-24 bg-gray-50">
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2.5 mb-4 justify-center">
            <span className="w-1 h-4" style={{ background: '#F47920' }} />
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#F47920' }}>
              Planos e Preços
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 tracking-tight">{heading}</h2>
          <p className="text-gray-500 max-w-xl mx-auto">{subheading}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch max-w-5xl mx-auto">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`border-2 rounded-xl p-8 flex flex-col transition-all duration-200 ${plan.style} ${plan.isPopular ? 'z-10 shadow-lg' : 'hover:shadow-md'}`}
              style={plan.styleObj || {}}
            >
              {plan.isPopular && (
                <div className="inline-flex items-center gap-1.5 bg-white/20 text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4 self-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Mais Popular
                </div>
              )}
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: plan.isPopular ? 'rgba(255,255,255,0.7)' : '#9ca3af' }}>{plan.tagline}</p>
              <h3 className="text-2xl font-extrabold mb-4" style={{ color: plan.isPopular ? 'white' : '#111827' }}>{plan.name}</h3>
              {!hidePrice && (
                <div className="mb-6">
                  <span className="text-4xl font-extrabold" style={{ color: plan.isPopular ? 'white' : '#111827' }}>{plan.price}</span>
                  <span className="text-sm ml-1" style={{ color: plan.isPopular ? 'rgba(255,255,255,0.6)' : '#9ca3af' }}>{plan.period}</span>
                </div>
              )}
              <ul className="space-y-3 mb-8 flex-1">
                {plan.features.map((feat, fi) => (
                  <li key={fi} className="flex items-center gap-2.5 text-sm" style={{ color: plan.isPopular ? 'rgba(255,255,255,0.85)' : '#4b5563' }}>
                    <CheckIcon inverted={plan.isPopular} />
                    {feat}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => document.querySelector('#contato')?.scrollIntoView({ behavior: 'smooth' })}
                className={`${plan.ctaStyle} font-bold px-6 py-3.5 rounded-lg transition-all duration-200 w-full text-sm shadow-sm`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-xs text-gray-400 mt-10">{footnote}</p>
      </div>
    </section>
  );
};

export default PlanosSection;
